import { useEffect, useState } from "react";
import { useTranslation } from "next-i18next";
import { Modal, Button } from "antd";
import { useRouter } from "next/router";
import { useAppSelector } from "@/store/hooks";
import { RootState } from "@/store/index";
import defaultSettings from "@/config/settings";
import Icon from "../Icon";
import InputItem from "./InputItem";
import { formatUSD } from "@/utils";
import { IAwardInfo } from "@/props/IDonation";

const DonationInfoConfirmModal = ({
  open,
  canEdit,
  onModalCancel,
  awardInfo,
  onModalSure,
}: {
  open: boolean;
  canEdit: boolean;
  onModalCancel: (open: boolean) => void;
  awardInfo: IAwardInfo;
  onModalSure: Function;
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const currentLocale: string | undefined = router.locale?.toUpperCase() || "EN";
  const [readAgreement, setReadAgreement] = useState<boolean>(canEdit ? false : true);
  const { pcModalWidth } = defaultSettings;
  const { isSmallScreen } = useAppSelector((state: RootState) => state.app);
  const appStore = useAppSelector((state: RootState) => state.app);
  const { isMobile } = appStore;
  const userInfo = useAppSelector((state) => state.user.userInfo);
  const [curName, setCurName] = useState<string>(userInfo?.localName || "");
  const [curEmail, setCurEmail] = useState<string>(userInfo?.uemail || "");
  const validateName = (v: string) => v.trim().length > 0;
  const validateEmail = (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v);

  useEffect(() => {
    if (awardInfo && awardInfo?.isSubmit) {
      setCurName(awardInfo?.realName);
      setCurEmail(awardInfo?.userEmail);
    }
  }, [awardInfo]);

  return (
    <>
      <Modal
        title={<p className="mb-4">{t("csr.donation_confirm_title")}</p>}
        open={open}
        footer={null}
        width={isSmallScreen ? 343 : pcModalWidth}
        onCancel={() => {
          onModalCancel(false);
        }}
        closeIcon={<Icon name="close" size={24} title="icon close" />}
        maskClosable={false}
      >
        <div className="border-top border-color-[#F1F1F1] border-t-[1px] py-6">
          <div className={`${isMobile ? "" : "mb-6"}`}>{t("csr.reward_wall_desc_2")}</div>
          <div className={`flex ${isMobile ? "flex-col-reverse" : ""}`}>
            <div>
              <InputItem
                canEdit={canEdit}
                info={{ title: t("csr.donation_name"), content: curName }}
                validator={validateName}
                onEditBlur={(newValue) => {
                  setCurName(newValue);
                }}
              />
              <div className="mt-[10px]">
                <InputItem
                  canEdit={canEdit}
                  info={{ title: t("csr.donation_email"), content: curEmail }}
                  validator={validateEmail}
                  onEditBlur={(newValue) => {
                    setCurEmail(newValue);
                  }}
                />
              </div>
            </div>
            <div
              className={`flex flex-1 flex-col justify-between border border-[#E6E8EB] bg-[#F1F1F1] ${
                isMobile ? "my-[10px] py-3" : "ml-5 py-4"
              }`}
            >
              <p className="ml-[10px] text-[#979797]">{t("csr.donation_amount")}</p>
              <div className="flex items-end justify-end pr-5 text-[#666666]">
                <span className="text-[32px]">{formatUSD(awardInfo?.totalDonationAmountUsd)}</span>
                <span className="ml-2 text-sm">USD</span>
              </div>
            </div>
          </div>
          <div className={`mt-[30px] w-full items-center justify-center`}>
            <div className="flex items-center">
              <Icon
                name={readAgreement ? "checked" : "check"}
                size={16}
                aria-hidden="true"
                onClick={() => {
                  if (!canEdit) return;
                  setReadAgreement(!readAgreement);
                }}
                data-qt="csr_donation_agreement_toggle"
                data-qt-checked={readAgreement ? 1 : 0}
              />
              <span className="ml-4">
                {t("csr.protocol_1")}
                <a
                  href={`${
                    currentLocale === "TC" ? "/files/donation_agreement_tc.pdf" : "/files/donation_agreement.pdf"
                  }`}
                  target="_blank"
                  className={`text-primary ${isMobile ? "block" : ""}`}
                  rel="noopener noreferrer"
                >
                  《{t("csr.protocol_2")}》
                </a>
              </span>
            </div>
            <div className={`flex justify-center ${isMobile ? "mt-8" : "mt-10"}`}>
              {canEdit ? (
                <>
                  <Button
                    type="primary"
                    className={`mr-4 min-w-[80px]`}
                    ghost
                    onClick={() => {
                      onModalCancel(false);
                    }}
                    style={{ height: "36px" }}
                    data-qt="csr_donation_info_cancel"
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="primary"
                    disabled={!readAgreement}
                    className="min-w-[80px]"
                    onClick={() => {
                      if (!readAgreement) {
                        return;
                      } else {
                        onModalSure({
                          eventId: 1,
                          realName: curName,
                          userEmail: curEmail,
                        });
                      }
                    }}
                    style={{ height: "36px" }}
                    data-qt="csr_donation_info_confirm"
                    data-qt-agreed={readAgreement ? 1 : 0}
                  >
                    {t("common.confirm")}
                  </Button>
                </>
              ) : (
                <Button
                  type="primary"
                  className="min-w-[80px]"
                  onClick={() => {
                    onModalCancel(false);
                  }}
                  style={{ height: "36px" }}
                >
                  {t("common.ok")}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default DonationInfoConfirmModal;
